import type { CanvasPoint, EntityStyleKind } from "./types";

export type AutomaticLifeLineSourceShape = {
  id: string;
  kind: string;
  points?: number[];
  closed?: boolean;
  isObstacle?: boolean;
  entityStyleKind?: EntityStyleKind;
  name?: string;
  roofGroupId?: string;
  roofGroupCategory?: "Flat" | "Curved" | "Pitched";
  roofTypeName?: string;
  roofPitch?: number;
  roofMaterialName?: string;
  roofSheetMaterialName?: string;
  fallSide?: boolean;
};

export type AutomaticLifeLinePath = {
  points: CanvasPoint[];
  closed: boolean;
  arcs?: AutomaticLifeLineArc[];
};

export type AutomaticLifeLineArc = {
  center: CanvasPoint;
  radius: number;
  startAngle: number;
  endAngle: number;
};

export type AutomaticLifeLineOptions = {
  flatRoofOffsetDistance?: number;
  ridgeOffsetDistance?: number;
  flatRoofOffsetSidePoint?: CanvasPoint | null;
};

const ROOF_PART_STYLE_KINDS = new Set<EntityStyleKind>([
  "roof-eave",
  "roof-ridge",
  "roof-hip",
  "roof-valley",
  "roof-hip-valley",
  "roof-rake",
]);

const ROOF_PART_NAME_PREFIXES = ["Gronda", "Colmo", "HIP", "Valley", "RAKE", "BL", "Diagonale"];

const POINT_EPSILON = 0.0001;
const DEFAULT_FLAT_ROOF_OFFSET_DISTANCE = 2000;
const DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE = 600;
const MAX_CURVED_ROOF_RAKE_TRIM_DISTANCE = 5000;
const MAX_SINGLE_PITCHED_SHORT_RAKE_LENGTH = 10000;
const DOUBLE_RIDGE_MATERIAL_PATTERNS = [
  "fake tiles",
  "ondulated metal sheet",
  "standing seam",
  "trapezoidal sandwich",
  "trapezoidal sheet",
];

const isSamePoint = (pointA: CanvasPoint, pointB: CanvasPoint) =>
  Math.abs(pointA.x - pointB.x) <= POINT_EPSILON && Math.abs(pointA.y - pointB.y) <= POINT_EPSILON;

const getDistance = (pointA: CanvasPoint, pointB: CanvasPoint) => Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);

const getPathLength = (points: CanvasPoint[]) => {
  let length = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index];
    const end = points[index + 1];
    if (!start || !end) continue;
    length += getDistance(start, end);
  }
  return length;
};

const getDistanceToSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= POINT_EPSILON) return getDistance(point, start);

  const ratio = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return getDistance(point, {
    x: start.x + dx * ratio,
    y: start.y + dy * ratio,
  });
};

const getClosestPointOnSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= POINT_EPSILON) return start;

  const ratio = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return {
    x: start.x + dx * ratio,
    y: start.y + dy * ratio,
  };
};

const getDistanceToPath = (point: CanvasPoint, path: CanvasPoint[]) => {
  if (path.length === 0) return Number.POSITIVE_INFINITY;
  const first = path[0];
  if (!first) return Number.POSITIVE_INFINITY;
  if (path.length === 1) return getDistance(point, first);

  let minDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < path.length - 1; index += 1) {
    const start = path[index];
    const end = path[index + 1];
    if (!start || !end) continue;
    minDistance = Math.min(minDistance, getDistanceToSegment(point, start, end));
  }
  return minDistance;
};

const getClosestPointOnPath = (point: CanvasPoint, path: CanvasPoint[]) => {
  const first = path[0];
  if (!first) return null;
  if (path.length === 1) return first;

  let closestPoint = first;
  let minDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < path.length - 1; index += 1) {
    const start = path[index];
    const end = path[index + 1];
    if (!start || !end) continue;

    const candidatePoint = getClosestPointOnSegment(point, start, end);
    const candidateDistance = getDistance(point, candidatePoint);
    if (candidateDistance < minDistance) {
      minDistance = candidateDistance;
      closestPoint = candidatePoint;
    }
  }

  return closestPoint;
};

const getPointAtDistanceAlongPath = (points: CanvasPoint[], distance: number) => {
  const first = points[0];
  if (!first) return null;
  if (distance <= POINT_EPSILON) return { point: first, segmentIndex: 0 };

  let coveredDistance = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index];
    const end = points[index + 1];
    if (!start || !end) continue;

    const segmentLength = getDistance(start, end);
    if (segmentLength <= POINT_EPSILON) continue;

    if (coveredDistance + segmentLength >= distance - POINT_EPSILON) {
      const ratio = Math.max(0, Math.min(1, (distance - coveredDistance) / segmentLength));
      return {
        point: {
          x: start.x + (end.x - start.x) * ratio,
          y: start.y + (end.y - start.y) * ratio,
        },
        segmentIndex: index,
      };
    }

    coveredDistance += segmentLength;
  }

  const lastIndex = points.length - 1;
  const last = points[lastIndex];
  return last ? { point: last, segmentIndex: Math.max(0, lastIndex - 1) } : null;
};

const trimOpenPath = (points: CanvasPoint[], startDistance: number, endDistance: number) => {
  const totalLength = getPathLength(points);
  if (points.length < 2 || totalLength <= POINT_EPSILON) return [];

  let safeStartDistance = Math.max(0, startDistance);
  let safeEndDistance = Math.max(0, endDistance);
  if (safeStartDistance + safeEndDistance >= totalLength) {
    const scale = Math.max(0, (totalLength - POINT_EPSILON) / (safeStartDistance + safeEndDistance || 1));
    safeStartDistance *= scale;
    safeEndDistance *= scale;
  }

  const start = getPointAtDistanceAlongPath(points, safeStartDistance);
  const end = getPointAtDistanceAlongPath(points, totalLength - safeEndDistance);
  if (!start || !end) return [];

  const trimmedPoints = [start.point];
  for (let index = start.segmentIndex + 1; index <= end.segmentIndex; index += 1) {
    const point = points[index];
    const previousPoint = trimmedPoints[trimmedPoints.length - 1];
    if (point && previousPoint && !isSamePoint(point, previousPoint)) {
      trimmedPoints.push(point);
    }
  }
  const previousPoint = trimmedPoints[trimmedPoints.length - 1];
  if (!previousPoint || !isSamePoint(end.point, previousPoint)) {
    trimmedPoints.push(end.point);
  }

  return getPathLength(trimmedPoints) > POINT_EPSILON ? trimmedPoints : [];
};

const getPolylinePoints = (shape: AutomaticLifeLineSourceShape): CanvasPoint[] => {
  const points = shape.points ?? [];
  const vertices: CanvasPoint[] = [];

  for (let index = 0; index <= points.length - 2; index += 2) {
    const point = { x: points[index] ?? 0, y: points[index + 1] ?? 0 };
    const previous = vertices[vertices.length - 1];
    if (previous && isSamePoint(previous, point)) continue;
    vertices.push(point);
  }

  return vertices;
};

const isPolylineSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.kind !== "circle" && shape.kind !== "arc" && !shape.isObstacle && (shape.points?.length ?? 0) >= 4;

const isRoofPartSource = (shape: AutomaticLifeLineSourceShape) =>
  (shape.entityStyleKind && ROOF_PART_STYLE_KINDS.has(shape.entityStyleKind)) ||
  ROOF_PART_NAME_PREFIXES.some((prefix) => shape.name?.startsWith(prefix));

const reversePoints = (points: CanvasPoint[]) => [...points].reverse();

const getSignedArea = (points: CanvasPoint[]) => {
  let area = 0;
  points.forEach((point, index) => {
    const nextPoint = points[(index + 1) % points.length];
    if (!nextPoint) return;
    area += point.x * nextPoint.y - nextPoint.x * point.y;
  });
  return area / 2;
};

const isPointInsidePolygon = (point: CanvasPoint, polygon: CanvasPoint[]) => {
  let inside = false;

  for (
    let currentIndex = 0, previousIndex = polygon.length - 1;
    currentIndex < polygon.length;
    previousIndex = currentIndex, currentIndex += 1
  ) {
    const current = polygon[currentIndex];
    const previous = polygon[previousIndex];
    if (!current || !previous) continue;

    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x;

    if (intersects) inside = !inside;
  }

  return inside;
};

const getSegmentNormal = (start: CanvasPoint, end: CanvasPoint, distance: number) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length <= POINT_EPSILON) return null;

  return {
    x: (-dy / length) * distance,
    y: (dx / length) * distance,
    direction: {
      x: dx / length,
      y: dy / length,
    },
  };
};

const getLineIntersection = (
  pointA: CanvasPoint,
  directionA: CanvasPoint,
  pointB: CanvasPoint,
  directionB: CanvasPoint,
) => {
  const denominator = directionA.x * directionB.y - directionA.y * directionB.x;
  if (Math.abs(denominator) <= 1e-9) return null;

  const deltaX = pointB.x - pointA.x;
  const deltaY = pointB.y - pointA.y;
  const factor = (deltaX * directionB.y - deltaY * directionB.x) / denominator;

  return {
    x: pointA.x + directionA.x * factor,
    y: pointA.y + directionA.y * factor,
  };
};

const getOffsetPathFromSegments = (
  vertices: CanvasPoint[],
  closed: boolean,
  getNormalDistance: (start: CanvasPoint, end: CanvasPoint) => number,
) => {
  if (vertices.length < 2) return [];

  const segmentCount = closed ? vertices.length : vertices.length - 1;
  const offsetSegments: Array<{
    start: CanvasPoint;
    end: CanvasPoint;
    direction: CanvasPoint;
  }> = [];

  for (let index = 0; index < segmentCount; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (!start || !end) return [];

    const normal = getSegmentNormal(start, end, getNormalDistance(start, end));
    if (!normal) return [];

    offsetSegments.push({
      start: {
        x: start.x + normal.x,
        y: start.y + normal.y,
      },
      end: {
        x: end.x + normal.x,
        y: end.y + normal.y,
      },
      direction: normal.direction,
    });
  }

  const offsetVertices: CanvasPoint[] = [];
  const pushVertex = (vertex: CanvasPoint) => {
    const previous = offsetVertices[offsetVertices.length - 1];
    if (previous && isSamePoint(previous, vertex)) return;
    offsetVertices.push(vertex);
  };

  if (!closed) {
    const firstSegment = offsetSegments[0];
    const lastSegment = offsetSegments[offsetSegments.length - 1];
    if (!firstSegment || !lastSegment) return [];

    pushVertex(firstSegment.start);
    for (let index = 1; index < offsetSegments.length; index += 1) {
      const previous = offsetSegments[index - 1];
      const current = offsetSegments[index];
      if (!previous || !current) return [];
      pushVertex(
        getLineIntersection(previous.start, previous.direction, current.start, current.direction) ?? current.start,
      );
    }
    pushVertex(lastSegment.end);
    return offsetVertices;
  }

  for (let index = 0; index < offsetSegments.length; index += 1) {
    const previous = offsetSegments[(index - 1 + offsetSegments.length) % offsetSegments.length];
    const current = offsetSegments[index];
    if (!previous || !current) return [];
    pushVertex(
      getLineIntersection(previous.start, previous.direction, current.start, current.direction) ?? current.start,
    );
  }

  const first = offsetVertices[0];
  const last = offsetVertices[offsetVertices.length - 1];
  if (first && last && isSamePoint(first, last)) offsetVertices.pop();
  return offsetVertices;
};

const isPathInsidePolygon = (path: CanvasPoint[], polygon: CanvasPoint[]) =>
  path.length > 0 && path.every((point) => isPointInsidePolygon(point, polygon));

const getOffsetClosedPath = (outline: CanvasPoint[], offsetDistance: number, sidePoint?: CanvasPoint | null) => {
  if (outline.length < 3) return [];

  const areaSign = getSignedArea(outline) >= 0 ? 1 : -1;
  const candidates = [
    getOffsetPathFromSegments(outline, true, () => offsetDistance * areaSign),
    getOffsetPathFromSegments(outline, true, () => -offsetDistance * areaSign),
  ].filter((candidate) => candidate.length >= 3);

  if (sidePoint) {
    const shouldOffsetInside = isPointInsidePolygon(sidePoint, outline);
    return (
      candidates.find((candidate) => isPathInsidePolygon(candidate, outline) === shouldOffsetInside) ??
      candidates[0] ??
      []
    );
  }

  return candidates.find((candidate) => isPathInsidePolygon(candidate, outline)) ?? candidates[0] ?? [];
};

const getOffsetOpenPath = (
  path: CanvasPoint[],
  outline: CanvasPoint[],
  offsetDistance: number,
  sidePoint?: CanvasPoint | null,
) => {
  if (path.length < 2 || outline.length < 3) return [];
  const shouldOffsetInside = sidePoint ? isPointInsidePolygon(sidePoint, outline) : true;

  return getOffsetPathFromSegments(path, false, (start, end) => {
    const normal = getSegmentNormal(start, end, 1);
    if (!normal) return offsetDistance;

    const midpoint = {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2,
    };
    const leftProbe = {
      x: midpoint.x + normal.x,
      y: midpoint.y + normal.y,
    };

    return isPointInsidePolygon(leftProbe, outline) === shouldOffsetInside ? offsetDistance : -offsetDistance;
  });
};

const appendPath = (target: CanvasPoint[], source: CanvasPoint[]) => {
  if (source.length === 0) return target;
  if (target.length === 0) return [...source];

  const lastTarget = target[target.length - 1];
  const firstSource = source[0];
  if (lastTarget && firstSource && isSamePoint(lastTarget, firstSource)) {
    return [...target, ...source.slice(1)];
  }

  return [...target, ...source];
};

const connectOpenPaths = (paths: CanvasPoint[][]): CanvasPoint[] => {
  const remaining = paths.filter((path) => path.length >= 2).map((path) => [...path]);
  const seed = remaining.shift();
  if (!seed) return [];

  let chain = seed;
  while (remaining.length > 0) {
    const chainEnd = chain[chain.length - 1];
    if (!chainEnd) break;

    let bestIndex = 0;
    let bestReverse = false;
    let bestDistance = Number.POSITIVE_INFINITY;

    remaining.forEach((path, index) => {
      const start = path[0];
      const end = path[path.length - 1];
      if (start) {
        const distance = getDistance(chainEnd, start);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
          bestReverse = false;
        }
      }
      if (end) {
        const distance = getDistance(chainEnd, end);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
          bestReverse = true;
        }
      }
    });

    const nextPath = remaining.splice(bestIndex, 1)[0] ?? [];
    chain = appendPath(chain, bestReverse ? reversePoints(nextPath) : nextPath);
  }

  return chain;
};

const isFlatRoofSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.roofGroupCategory === "Flat" ||
  shape.entityStyleKind === "rectangle" ||
  shape.entityStyleKind === "custom-flat-roof" ||
  shape.name?.startsWith("Flat Roof") ||
  shape.name?.startsWith("Custom Flat Roof") ||
  shape.name?.startsWith("Custom Flat Room");

const isFlatRoofOutlineSource = (shape: AutomaticLifeLineSourceShape) =>
  isPolylineSource(shape) && !!shape.closed && (shape.points?.length ?? 0) >= 6 && isFlatRoofSource(shape);

const isCurvedRoofSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.roofGroupCategory === "Curved" ||
  shape.entityStyleKind === "curved-roof" ||
  shape.name?.startsWith("Curved Roof");

const isGableRoofSource = (shape: AutomaticLifeLineSourceShape) => shape.entityStyleKind === "single-gable-roof";

const isMultiGableRoofSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "multi-gable-roof";

const isOffsetRidgeRoofSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "single-pitched-roof" || shape.entityStyleKind === "multi-shed-roof";

const isSinglePitchedRoofSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "single-pitched-roof";

const isTrimmedRidgeRoofSource = (shape: AutomaticLifeLineSourceShape) =>
  isCurvedRoofSource(shape) || isGableRoofSource(shape);

const isHipOrValleySource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "roof-hip" ||
  shape.entityStyleKind === "roof-valley" ||
  shape.entityStyleKind === "roof-hip-valley" ||
  shape.name?.startsWith("HIP") ||
  shape.name?.startsWith("Valley") ||
  shape.name?.startsWith("Diagonale");

const normalizeRuleText = (value: string | undefined) =>
  (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const shouldDoubleMetalRoofRidgeLifeLine = (shapes: AutomaticLifeLineSourceShape[]) =>
  shapes.some((shape) => {
    const roofText = normalizeRuleText(
      [shape.roofTypeName, shape.roofMaterialName, shape.roofSheetMaterialName].filter(Boolean).join(" "),
    );
    return DOUBLE_RIDGE_MATERIAL_PATTERNS.some((pattern) => roofText.includes(pattern));
  });

const buildOffsetOpenPath = (path: CanvasPoint[], offsetDistance: number) =>
  getOffsetPathFromSegments(path, false, () => offsetDistance);

const buildSideSelectedOffsetOpenPath = (
  path: CanvasPoint[],
  offsetDistance: number,
  sidePoint?: CanvasPoint | null,
) => {
  const candidates = [
    buildOffsetOpenPath(path, offsetDistance),
    buildOffsetOpenPath(path, -offsetDistance),
  ].filter((candidate) => candidate.length >= 2);

  if (candidates.length === 0) return [];
  if (!sidePoint) return candidates[0] ?? [];

  return candidates.reduce((best, candidate) =>
    getDistanceToPath(sidePoint, candidate) < getDistanceToPath(sidePoint, best) ? candidate : best,
  );
};

const getClosestRakeDistance = (point: CanvasPoint, rakePaths: CanvasPoint[][]) =>
  rakePaths.reduce((bestDistance, rakePath) => Math.min(bestDistance, getDistanceToPath(point, rakePath)), Number.POSITIVE_INFINITY);

const isPointOnRake = (point: CanvasPoint, rakePaths: CanvasPoint[][]) => getClosestRakeDistance(point, rakePaths) <= 1;

const getSegmentIntersection = (
  startA: CanvasPoint,
  endA: CanvasPoint,
  startB: CanvasPoint,
  endB: CanvasPoint,
) => {
  const directionA = { x: endA.x - startA.x, y: endA.y - startA.y };
  const directionB = { x: endB.x - startB.x, y: endB.y - startB.y };
  const denominator = directionA.x * directionB.y - directionA.y * directionB.x;

  if (Math.abs(denominator) <= 1e-9) {
    return null;
  }

  const delta = { x: startB.x - startA.x, y: startB.y - startA.y };
  const ratioA = (delta.x * directionB.y - delta.y * directionB.x) / denominator;
  const ratioB = (delta.x * directionA.y - delta.y * directionA.x) / denominator;

  if (ratioA < -POINT_EPSILON || ratioA > 1 + POINT_EPSILON || ratioB < -POINT_EPSILON || ratioB > 1 + POINT_EPSILON) {
    return null;
  }

  return {
    x: startA.x + directionA.x * Math.max(0, Math.min(1, ratioA)),
    y: startA.y + directionA.y * Math.max(0, Math.min(1, ratioA)),
  };
};

const getPointRatioOnSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= POINT_EPSILON) return 0;

  return ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared;
};

const pushUniquePoint = (points: CanvasPoint[], point: CanvasPoint) => {
  if (points.some((existingPoint) => isSamePoint(existingPoint, point))) return;
  points.push(point);
};

const getUnitDirection = (start: CanvasPoint, end: CanvasPoint) => {
  const distance = getDistance(start, end);
  if (distance <= POINT_EPSILON) return null;

  return {
    x: (end.x - start.x) / distance,
    y: (end.y - start.y) / distance,
  };
};

const buildTerminalArc = (
  center: CanvasPoint,
  outwardDirection: CanvasPoint,
  radius: number,
): AutomaticLifeLineArc | null => {
  if (radius <= POINT_EPSILON) return null;

  const outwardAngle = Math.atan2(outwardDirection.y, outwardDirection.x);
  return {
    center,
    radius,
    startAngle: outwardAngle - Math.PI / 2,
    endAngle: outwardAngle + Math.PI / 2,
  };
};

const isRidgeSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "roof-ridge" || shape.name?.startsWith("Colmo");

const isEaveSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "roof-eave" || shape.name?.startsWith("Gronda");

const isRakeSource = (shape: AutomaticLifeLineSourceShape) =>
  shape.entityStyleKind === "roof-rake" || shape.name?.startsWith("RAKE") || shape.name?.startsWith("BL");

const isSameRoofContext = (shape: AutomaticLifeLineSourceShape, outline: AutomaticLifeLineSourceShape) => {
  if (shape.id === outline.id) return false;
  if (shape.roofGroupId && outline.roofGroupId) return shape.roofGroupId === outline.roofGroupId;
  return true;
};

const buildFlatRoofFallSidePaths = (
  shapes: AutomaticLifeLineSourceShape[],
  offsetDistance: number,
  offsetSidePoint?: CanvasPoint | null,
): AutomaticLifeLinePath[] => {
  const safeOffsetDistance = Math.max(0, offsetDistance);
  if (safeOffsetDistance <= 0) return [];

  const polylineSources = shapes.filter(isPolylineSource);
  const flatRoofOutlines = polylineSources.filter(isFlatRoofOutlineSource);
  const paths: AutomaticLifeLinePath[] = [];

  flatRoofOutlines.forEach((outlineShape) => {
    const outline = getPolylinePoints(outlineShape);
    if (outline.length < 3) return;

    const fallSideRoofParts = polylineSources.filter(
      (shape) => isSameRoofContext(shape, outlineShape) && !shape.closed && shape.fallSide === true,
    );

    if (fallSideRoofParts.length > 0) {
      const offsetOpenPaths = fallSideRoofParts
        .map((shape) => getOffsetOpenPath(getPolylinePoints(shape), outline, safeOffsetDistance, offsetSidePoint))
        .filter((path) => path.length >= 2);

      const connectedPath = connectOpenPaths(offsetOpenPaths);
      if (connectedPath.length >= 2) {
        paths.push({ points: connectedPath, closed: false });
      }
      return;
    }

    if (outlineShape.fallSide !== true) return;

    const offsetOutline = getOffsetClosedPath(outline, safeOffsetDistance, offsetSidePoint);
    if (offsetOutline.length >= 3) {
      paths.push({ points: offsetOutline, closed: true });
    }
  });

  return paths;
};

const getCurvedRoofEndpointTrimDistance = (
  endpoint: CanvasPoint,
  _ridgeLength: number,
  rakePaths: CanvasPoint[][],
  excludedRakeIndex?: number,
) => {
  const rankedRakes = rakePaths
    .map((rakePath, index) => ({
      index,
      length: getPathLength(rakePath),
      distance: getDistanceToPath(endpoint, rakePath),
    }))
    .filter((rake) => rake.index !== excludedRakeIndex && rake.length > POINT_EPSILON)
    .sort((left, right) => left.distance - right.distance);

  const rake = rankedRakes[0];
  return rake
    ? {
        rakeIndex: rake.index,
        trimDistance: Math.min(rake.length / 2, MAX_CURVED_ROOF_RAKE_TRIM_DISTANCE),
      }
    : null;
};

const buildTrimmedRidgeRoofPaths = (shapes: AutomaticLifeLineSourceShape[]): AutomaticLifeLinePath[] => {
  const polylineSources = shapes.filter(isPolylineSource);
  const trimmedRidgeRoofGroupIds = new Set(
    polylineSources
      .filter((shape) => isTrimmedRidgeRoofSource(shape) && shape.roofGroupId)
      .map((shape) => shape.roofGroupId as string),
  );
  const includeUngroupedTrimmedRidgeRoof = polylineSources.some(
    (shape) => isTrimmedRidgeRoofSource(shape) && !shape.roofGroupId,
  );
  const groupKeys = [...trimmedRidgeRoofGroupIds, ...(includeUngroupedTrimmedRidgeRoof ? [""] : [])];
  const paths: AutomaticLifeLinePath[] = [];

  for (const groupKey of groupKeys) {
    const groupShapes = groupKey
      ? polylineSources.filter((shape) => shape.roofGroupId === groupKey)
      : polylineSources.filter((shape) => !shape.roofGroupId && isTrimmedRidgeRoofSource(shape));
    const ridgeSources = groupShapes.filter((shape) => !shape.closed && isRidgeSource(shape));
    const rakePaths = groupShapes
      .filter((shape) => !shape.closed && isRakeSource(shape))
      .map(getPolylinePoints)
      .filter((rakePath) => getPathLength(rakePath) > POINT_EPSILON);
    const doubleGableRidgeLifeLine = shouldDoubleMetalRoofRidgeLifeLine(groupShapes);

    for (const ridgeSource of ridgeSources) {
      const ridgePath = getPolylinePoints(ridgeSource);
      const ridgeStart = ridgePath[0];
      const ridgeEnd = ridgePath[ridgePath.length - 1];
      const ridgeLength = getPathLength(ridgePath);
      if (!ridgeStart || !ridgeEnd || ridgeLength <= POINT_EPSILON) continue;
      const ridgeDirection = getUnitDirection(ridgeStart, ridgeEnd);
      if (!ridgeDirection) continue;

      const startTrim = getCurvedRoofEndpointTrimDistance(ridgeStart, ridgeLength, rakePaths);
      const endTrim = getCurvedRoofEndpointTrimDistance(ridgeEnd, ridgeLength, rakePaths, startTrim?.rakeIndex);
      const startTrimDistance = doubleGableRidgeLifeLine
        ? Math.max(0, (startTrim?.trimDistance ?? 0) + DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE)
        : (startTrim?.trimDistance ?? 0);
      const endTrimDistance = doubleGableRidgeLifeLine
        ? Math.max(0, (endTrim?.trimDistance ?? 0) + DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE)
        : (endTrim?.trimDistance ?? 0);
      const trimmedPath = trimOpenPath(ridgePath, startTrimDistance, endTrimDistance);
      if (trimmedPath.length < 2) continue;
      const startLifeLineTerminal = trimmedPath[0];
      const endLifeLineTerminal = trimmedPath[trimmedPath.length - 1];
      if (!startLifeLineTerminal || !endLifeLineTerminal) continue;

      const terminalArcs = [
        buildTerminalArc(startLifeLineTerminal, { x: -ridgeDirection.x, y: -ridgeDirection.y }, startTrimDistance),
        buildTerminalArc(endLifeLineTerminal, ridgeDirection, endTrimDistance),
      ].filter((arc): arc is AutomaticLifeLineArc => arc != null);

      if (doubleGableRidgeLifeLine) {
        const offsetPaths = [
          buildOffsetOpenPath(trimmedPath, DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE),
          buildOffsetOpenPath(trimmedPath, -DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE),
        ].filter((offsetPath) => offsetPath.length >= 2);

        paths.push(
          ...offsetPaths.map((offsetPath, index) => ({
            points: offsetPath,
            closed: false,
            arcs: index === 0 ? terminalArcs : undefined,
          })),
        );
      } else {
        paths.push({ points: trimmedPath, closed: false, arcs: terminalArcs });
      }
    }
  }

  return paths;
};

const getRoofPitch = (shapes: AutomaticLifeLineSourceShape[]) =>
  shapes.find((shape) => typeof shape.roofPitch === "number" && Number.isFinite(shape.roofPitch))?.roofPitch;

const getSinglePitchedShortRakeRule = (rakePaths: CanvasPoint[][]) => {
  const rakeLengths = rakePaths.map(getPathLength).filter((length) => length > POINT_EPSILON);
  if (
    rakeLengths.length === 0 ||
    rakeLengths.some((length) => length > MAX_SINGLE_PITCHED_SHORT_RAKE_LENGTH)
  ) {
    return null;
  }

  const averageRakeHalfLength =
    rakeLengths.reduce((sum, length) => sum + length / 2, 0) / rakeLengths.length;

  return {
    offsetDistance: averageRakeHalfLength,
    getTrimDistance: (endpoint: CanvasPoint) => {
      const closestRake = rakePaths
        .map((rakePath) => ({
          length: getPathLength(rakePath),
          distance: getDistanceToPath(endpoint, rakePath),
        }))
        .filter((rake) => rake.length > POINT_EPSILON)
        .sort((left, right) => left.distance - right.distance)[0];

      return closestRake ? closestRake.length / 2 : averageRakeHalfLength;
    },
  };
};

const buildOffsetOpenPathTowardEave = (
  path: CanvasPoint[],
  offsetDistance: number,
  eavePaths: CanvasPoint[][],
  sidePoint?: CanvasPoint | null,
) => {
  const eaveReferencePoint = eavePaths
    .flat()
    .reduce<CanvasPoint | null>(
      (sum, point, index) =>
        sum
          ? {
              x: (sum.x * index + point.x) / (index + 1),
              y: (sum.y * index + point.y) / (index + 1),
            }
          : point,
      null,
    );

  return buildSideSelectedOffsetOpenPath(path, offsetDistance, eaveReferencePoint ?? sidePoint);
};

const buildLowPitchSinglePitchedPath = (
  mainPath: CanvasPoint[],
  eavePaths: CanvasPoint[][],
  offsetDistance: number,
) => {
  const start = mainPath[0];
  const end = mainPath[mainPath.length - 1];
  if (!start || !end || eavePaths.length === 0) return mainPath;

  const buildEaveStop = (terminal: CanvasPoint) => {
    const closestEavePoint = eavePaths
      .map((eavePath) => getClosestPointOnPath(terminal, eavePath))
      .filter((point): point is CanvasPoint => point != null)
      .sort((left, right) => getDistance(terminal, left) - getDistance(terminal, right))[0];
    if (!closestEavePoint) return null;

    const terminalToEaveDistance = getDistance(terminal, closestEavePoint);
    if (terminalToEaveDistance <= offsetDistance + POINT_EPSILON) return null;

    const ratio = (terminalToEaveDistance - offsetDistance) / terminalToEaveDistance;
    return {
      x: terminal.x + (closestEavePoint.x - terminal.x) * ratio,
      y: terminal.y + (closestEavePoint.y - terminal.y) * ratio,
    };
  };

  const startEaveStop = buildEaveStop(start);
  const endEaveStop = buildEaveStop(end);
  if (!startEaveStop && !endEaveStop) return mainPath;

  return [
    ...(startEaveStop ? [startEaveStop] : []),
    ...mainPath,
    ...(endEaveStop ? [endEaveStop] : []),
  ];
};

const buildOffsetRidgeRoofPaths = (
  shapes: AutomaticLifeLineSourceShape[],
  offsetDistance: number,
  offsetSidePoint?: CanvasPoint | null,
): AutomaticLifeLinePath[] => {
  const safeOffsetDistance = Math.max(0, offsetDistance);
  if (safeOffsetDistance <= POINT_EPSILON) return [];

  const polylineSources = shapes.filter(isPolylineSource);
  const groupKeys = Array.from(
    new Set(
      polylineSources
        .filter((shape) => isOffsetRidgeRoofSource(shape) || isRidgeSource(shape))
        .map((shape) => shape.roofGroupId ?? ""),
    ),
  );
  const paths: AutomaticLifeLinePath[] = [];

  for (const groupKey of groupKeys) {
    const groupShapes = groupKey
      ? polylineSources.filter((shape) => shape.roofGroupId === groupKey)
      : polylineSources.filter((shape) => !shape.roofGroupId);
    if (!groupShapes.some(isOffsetRidgeRoofSource)) continue;

    const isSinglePitchedRoof = groupShapes.some(isSinglePitchedRoofSource);
    const eavePaths = groupShapes
      .filter((shape) => !shape.closed && isEaveSource(shape))
      .map(getPolylinePoints)
      .filter((path) => getPathLength(path) > POINT_EPSILON);
    const rakePaths = groupShapes
      .filter((shape) => !shape.closed && isRakeSource(shape))
      .map(getPolylinePoints)
      .filter((path) => getPathLength(path) > POINT_EPSILON);
    const shortRakeRule = isSinglePitchedRoof ? getSinglePitchedShortRakeRule(rakePaths) : null;
    const roofPitch = getRoofPitch(groupShapes);
    const shouldAddLowPitchEaveSegments =
      isSinglePitchedRoof && shortRakeRule == null && roofPitch != null && roofPitch < 15;
    const ridgePaths = groupShapes
      .filter((shape) => !shape.closed && isRidgeSource(shape))
      .map(getPolylinePoints)
      .filter((path) => getPathLength(path) > POINT_EPSILON);

    for (const ridgePath of ridgePaths) {
      const ridgeStart = ridgePath[0];
      const ridgeEnd = ridgePath[ridgePath.length - 1];
      if (!ridgeStart || !ridgeEnd) continue;

      const startTrimDistance = shortRakeRule?.getTrimDistance(ridgeStart) ?? safeOffsetDistance;
      const endTrimDistance = shortRakeRule?.getTrimDistance(ridgeEnd) ?? safeOffsetDistance;
      const mainOffsetDistance = shortRakeRule?.offsetDistance ?? safeOffsetDistance;
      const trimmedPath = trimOpenPath(ridgePath, startTrimDistance, endTrimDistance);
      if (trimmedPath.length < 2) continue;

      const offsetPath = isSinglePitchedRoof
        ? buildOffsetOpenPathTowardEave(trimmedPath, mainOffsetDistance, eavePaths, offsetSidePoint)
        : buildSideSelectedOffsetOpenPath(trimmedPath, mainOffsetDistance, offsetSidePoint);
      if (offsetPath.length >= 2) {
        paths.push({
          points: shouldAddLowPitchEaveSegments
            ? buildLowPitchSinglePitchedPath(offsetPath, eavePaths, safeOffsetDistance)
            : offsetPath,
          closed: false,
        });
      }
    }
  }

  return paths;
};

const isHipRoofGroup = (groupShapes: AutomaticLifeLineSourceShape[]) =>
  groupShapes.some(isRidgeSource) &&
  groupShapes.some(isHipOrValleySource) &&
  !groupShapes.some((shape) => shape.entityStyleKind === "multi-gable-roof");

const buildHipRoofRidgePaths = (shapes: AutomaticLifeLineSourceShape[]): AutomaticLifeLinePath[] => {
  const polylineSources = shapes.filter(isPolylineSource);
  const groupKeys = Array.from(new Set(polylineSources.map((shape) => shape.roofGroupId ?? "")));
  const paths: AutomaticLifeLinePath[] = [];

  for (const groupKey of groupKeys) {
    const groupShapes = groupKey
      ? polylineSources.filter((shape) => shape.roofGroupId === groupKey)
      : polylineSources.filter((shape) => !shape.roofGroupId);
    if (!isHipRoofGroup(groupShapes)) continue;

    const doubleRidgeLifeLine = shouldDoubleMetalRoofRidgeLifeLine(groupShapes);
    const ridgePaths = groupShapes
      .filter((shape) => !shape.closed && isRidgeSource(shape))
      .map(getPolylinePoints)
      .filter((path) => getPathLength(path) > POINT_EPSILON);

    for (const ridgePath of ridgePaths) {
      if (doubleRidgeLifeLine) {
        const offsetPaths = [
          buildOffsetOpenPath(ridgePath, DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE),
          buildOffsetOpenPath(ridgePath, -DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE),
        ].filter((offsetPath) => offsetPath.length >= 2);

        paths.push(...offsetPaths.map((offsetPath) => ({ points: offsetPath, closed: false })));
      } else {
        paths.push({ points: ridgePath, closed: false });
      }
    }
  }

  return paths;
};

const buildMultiGableRoofRidgePaths = (shapes: AutomaticLifeLineSourceShape[]): AutomaticLifeLinePath[] => {
  const polylineSources = shapes.filter(isPolylineSource);
  const groupKeys = Array.from(
    new Set(
      polylineSources
        .filter((shape) => isMultiGableRoofSource(shape) || isRidgeSource(shape) || isRakeSource(shape))
        .map((shape) => shape.roofGroupId ?? ""),
    ),
  );
  const paths: AutomaticLifeLinePath[] = [];

  for (const groupKey of groupKeys) {
    const groupShapes = groupKey
      ? polylineSources.filter((shape) => shape.roofGroupId === groupKey)
      : polylineSources.filter((shape) => !shape.roofGroupId);
    if (!groupShapes.some(isMultiGableRoofSource)) continue;

    const ridgeSegments = groupShapes
      .filter((shape) => !shape.closed && isRidgeSource(shape))
      .map((shape) => {
        const points = getPolylinePoints(shape);
        const start = points[0];
        const end = points[points.length - 1];
        return start && end && !isSamePoint(start, end) ? { start, end } : null;
      })
      .filter((segment): segment is { start: CanvasPoint; end: CanvasPoint } => segment != null);
    const rakePaths = groupShapes
      .filter((shape) => !shape.closed && isRakeSource(shape))
      .map(getPolylinePoints)
      .filter((path) => getPathLength(path) > POINT_EPSILON);
    const doubleRidgeLifeLine = shouldDoubleMetalRoofRidgeLifeLine(groupShapes);

    ridgeSegments.forEach((ridgeSegment, ridgeIndex) => {
      const splitPoints: CanvasPoint[] = [ridgeSegment.start, ridgeSegment.end];

      ridgeSegments.forEach((otherSegment, otherIndex) => {
        if (ridgeIndex === otherIndex) return;
        const intersection = getSegmentIntersection(
          ridgeSegment.start,
          ridgeSegment.end,
          otherSegment.start,
          otherSegment.end,
        );
        if (intersection) pushUniquePoint(splitPoints, intersection);
      });

      const ridgePath = splitPoints.sort(
        (left, right) =>
          getPointRatioOnSegment(left, ridgeSegment.start, ridgeSegment.end) -
          getPointRatioOnSegment(right, ridgeSegment.start, ridgeSegment.end),
      );
      if (ridgePath.length < 2) return;

      const ridgeLength = getPathLength(ridgePath);
      const ridgeStart = ridgePath[0];
      const ridgeEnd = ridgePath[ridgePath.length - 1];
      if (!ridgeStart || !ridgeEnd || ridgeLength <= POINT_EPSILON) return;

      const startTrim = isPointOnRake(ridgeStart, rakePaths)
        ? (getCurvedRoofEndpointTrimDistance(ridgeStart, ridgeLength, rakePaths)?.trimDistance ?? 0)
        : 0;
      const endTrim = isPointOnRake(ridgeEnd, rakePaths)
        ? (getCurvedRoofEndpointTrimDistance(ridgeEnd, ridgeLength, rakePaths)?.trimDistance ?? 0)
        : 0;
      const startTrimDistance = doubleRidgeLifeLine
        ? Math.max(0, startTrim + DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE)
        : startTrim;
      const endTrimDistance = doubleRidgeLifeLine
        ? Math.max(0, endTrim + DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE)
        : endTrim;
      const trimmedPath = trimOpenPath(ridgePath, startTrimDistance, endTrimDistance);
      if (trimmedPath.length < 2) return;

      if (doubleRidgeLifeLine) {
        const offsetPaths = [
          buildOffsetOpenPath(trimmedPath, DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE),
          buildOffsetOpenPath(trimmedPath, -DEFAULT_GABLE_RIDGE_LIFELINE_OFFSET_DISTANCE),
        ].filter((offsetPath) => offsetPath.length >= 2);

        paths.push(...offsetPaths.map((offsetPath) => ({ points: offsetPath, closed: false })));
      } else {
        paths.push({ points: trimmedPath, closed: false });
      }
    });
  }

  return paths;
};

export const buildAutomaticLifeLinePaths = (
  shapes: AutomaticLifeLineSourceShape[],
  options: AutomaticLifeLineOptions = {},
): AutomaticLifeLinePath[] => {
  const flatRoofPaths = buildFlatRoofFallSidePaths(
    shapes,
    options.flatRoofOffsetDistance ?? DEFAULT_FLAT_ROOF_OFFSET_DISTANCE,
    options.flatRoofOffsetSidePoint,
  );
  if (flatRoofPaths.length > 0) return flatRoofPaths;

  const offsetRidgeRoofPaths = buildOffsetRidgeRoofPaths(
    shapes,
    options.ridgeOffsetDistance ?? options.flatRoofOffsetDistance ?? DEFAULT_FLAT_ROOF_OFFSET_DISTANCE,
    options.flatRoofOffsetSidePoint,
  );
  if (offsetRidgeRoofPaths.length > 0) return offsetRidgeRoofPaths;

  const trimmedRidgeRoofPaths = buildTrimmedRidgeRoofPaths(shapes);
  if (trimmedRidgeRoofPaths.length > 0) return trimmedRidgeRoofPaths;

  const multiGableRoofRidgePaths = buildMultiGableRoofRidgePaths(shapes);
  if (multiGableRoofRidgePaths.length > 0) return multiGableRoofRidgePaths;

  const hipRoofRidgePaths = buildHipRoofRidgePaths(shapes);
  if (hipRoofRidgePaths.length > 0) return hipRoofRidgePaths;

  const polylineSources = shapes.filter(isPolylineSource);
  const roofPartSources = polylineSources.filter(isRoofPartSource);

  if (roofPartSources.length > 0) {
    const connectedPath = connectOpenPaths(roofPartSources.map(getPolylinePoints));
    return connectedPath.length >= 2 ? [{ points: connectedPath, closed: false }] : [];
  }

  const roofOutlinePaths = polylineSources
    .filter((shape) => shape.closed && (shape.points?.length ?? 0) >= 6)
    .map(getPolylinePoints)
    .filter((points) => points.length >= 3)
    .map((points) => ({ points, closed: true }));

  if (roofOutlinePaths.length > 0) return roofOutlinePaths;

  return polylineSources
    .map(getPolylinePoints)
    .filter((points) => points.length >= 2)
    .map((points) => ({ points, closed: false }));
};
